// История чатов. SMY-75, переписано в SMY-116
//
// Раньше здесь были проекты сеткой и таблица результатов. Проекты убраны:
// папка вокруг цели оказалась лишним уровнем — чат заводится на каждый
// вопрос и называется этим вопросом, а раскладывать их по папкам никто не
// стал. Таблица результатов ушла следом: результат живёт в чате, и строка,
// ведущая мимо разговора, вела в половину истории.
//
// Осталось то, ради чего сюда приходят: все чаты, поиск по названию и архив.
// Колонка слева показывает пять последних, этот экран — всё остальное.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { attempts, type ChatSummary } from '@smyslika/api-client'

import { agoLabel } from '@smyslika/core'
import { useSession } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'
import { Shell } from '../shell/Shell.tsx'
import { Caps, Confirm, Icon } from '../ui/index.ts'

/** Строка чата: название, когда трогали, действия. */
function Row({
  chat,
  now,
  onOpen,
  onAct,
}: {
  chat: ChatSummary
  now: Date
  onOpen: () => void
  onAct: (what: 'pin' | 'unpin' | 'archive' | 'restore' | 'delete') => void
}) {
  return (
    <div className="chats__row">
      <button type="button" className="chats__open" onClick={onOpen}>
        {chat.pinned === true ? <Icon name="pin" size={14} strokeWidth={1.9} /> : null}
        <span className="chats__title">{chat.title ?? 'Без названия'}</span>
        <span className="chats__when">{agoLabel(chat.lastMessageAt ?? chat.createdAt, now)}</span>
      </button>

      <div className="chats__actions">
        {chat.archived === true ? (
          <button type="button" className="btn btn--ghost" onClick={() => onAct('restore')}>
            Вернуть
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => onAct(chat.pinned === true ? 'unpin' : 'pin')}
            >
              {chat.pinned === true ? 'Открепить' : 'Закрепить'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => onAct('archive')}>
              В архив
            </button>
          </>
        )}
        <button type="button" className="btn btn--ghost chats__danger" onClick={() => onAct('delete')}>
          Удалить
        </button>
      </div>
    </div>
  )
}

export function History() {
  const { chats, refresh } = useWorkspace()
  const { chats: api, serverNow } = useSession()
  const navigate = useNavigate()

  const [archived, setArchived] = useState<ChatSummary[]>([])
  const [query, setQuery] = useState('')
  /**
   * Что подтверждаем. Удаление и архив спрашивают, остальное — нет: первые
   * два убирают чат из-под глаз, а закрепление снимается тем же жестом.
   */
  const [ask, setAsk] = useState<{ id: string; what: 'archive' | 'delete' } | null>(null)
  const now = serverNow()

  // Архив читается отдельным запросом и только на этом экране: колонке слева
  // он не нужен, а тащить его в общий список значило бы возвращать в неё то,
  // что человек оттуда и убрал.
  const loadArchived = async (): Promise<void> => {
    try {
      setArchived((await api.list({ archived: true })).chats)
    } catch {
      setArchived([])
    }
  }

  useEffect(() => {
    void loadArchived()
    // Один раз при открытии: архив меняется только отсюда, и следить за ним
    // подпиской незачем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const act = async (id: string, what: 'pin' | 'unpin' | 'archive' | 'restore' | 'delete'): Promise<void> => {
    try {
      if (what === 'delete') {
        await api.remove(id)
        // Попытке удалённого чата возвращаться некуда (SMY-449).
        attempts.forgetChat(id)
      }
      else if (what === 'archive') await api.update(id, { archived: true })
      else if (what === 'restore') await api.update(id, { archived: false })
      else await api.update(id, { pinned: what === 'pin' })
    } catch {
      // Список просто останется прежним: страница об ошибке поверх истории
      // хуже, чем не сработавшая кнопка.
    }
    await Promise.all([refresh(), loadArchived()])
  }

  const match = (c: ChatSummary): boolean => {
    const needle = query.trim().toLowerCase()
    return needle === '' || (c.title ?? '').toLowerCase().includes(needle)
  }

  const live = useMemo(() => chats.filter(match), [chats, query])
  const inArchive = useMemo(() => archived.filter(match), [archived, query])

  return (
    <Shell account>
      <div className="content account">
        <h1 className="account__title">История чатов</h1>

        <div className="account__filters">
          <div className="account__search">
            <Icon name="search" size={16} />
            <input
              className="account__search-field"
              value={query}
              aria-label="Поиск по чатам"
              placeholder="Поиск по чатам"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {live.length === 0 ? (
          <p className="muted account__empty">
            {chats.length === 0
              ? 'Чатов пока нет. Каждый новый вопрос заводит свой — он появится здесь и в колонке слева.'
              : 'Под этот поиск ничего не подходит. Попробуй другими словами.'}
          </p>
        ) : (
          <div className="chats">
            {live.map((c) => (
              <Row
                key={c.id}
                chat={c}
                now={now}
                onOpen={() => navigate(`/chat/${c.id}`)}
                onAct={(what) =>
                  what === 'archive' || what === 'delete'
                    ? setAsk({ id: c.id, what })
                    : void act(c.id, what)
                }
              />
            ))}
          </div>
        )}

        {/* Архив показывается, только когда в нём что-то есть: пустой раздел
            «Архив» на экране у человека, который им не пользуется, — это
            обещание функции, которой он не просил. */}
        {inArchive.length > 0 ? (
          <>
            <Caps>Архив</Caps>
            <div className="chats chats--archived">
              {inArchive.map((c) => (
                <Row
                  key={c.id}
                  chat={c}
                  now={now}
                  onOpen={() => navigate(`/chat/${c.id}`)}
                  onAct={(what) =>
                    what === 'archive' || what === 'delete'
                      ? setAsk({ id: c.id, what })
                      : void act(c.id, what)
                  }
                />
              ))}
            </div>
          </>
        ) : null}

        {ask != null ? (
          <Confirm
            open
            title={ask.what === 'delete' ? 'Удалить чат?' : 'Убрать чат в архив?'}
            action={ask.what === 'delete' ? 'Удалить' : 'В архив'}
            {...(ask.what === 'delete' ? { danger: true } : {})}
            onCancel={() => setAsk(null)}
            onConfirm={() => {
              const { id, what } = ask
              setAsk(null)
              void act(id, what)
            }}
          >
            {ask.what === 'delete'
              ? 'Вместе с ним удалится вся переписка, и восстановить её будет неоткуда. История списаний останется.'
              : 'Он исчезнет из колонки слева, но останется здесь — вернуть можно одним нажатием.'}
          </Confirm>
        ) : null}
      </div>
    </Shell>
  )
}
